import { defineStore } from 'pinia'
import { hideShowFullSpinner } from 'src/utils'
import { Platform } from 'quasar'
import { NotifyMethods } from 'src/boot/notify'
import { useCartStore } from 'src/stores/cart'
import { usePersistedStore } from 'src/stores/persisted'
import { useCommonStore } from 'src/stores/common'

export const useOrderStore = defineStore('order', {
  state: () => ({
    orders: {},
    recentOrderNumber: null,
    detail: {},
    trackOrderDetail: {},
    editDetail: {},
    subscriptionDetail: {},
    isGiftCardAllowForRecurringOrder: false,

    // update order
    categories: [],
    recentItems: {},
    recentUpdatedOrder: null,
    alertInfo: {},
    updateTimeSlot: {},

    parkingSpots: null,
    slotInfo: {},
    loadingSlot: false,
    filterSearchBy: '',
  }),
  getters: {
    orderDetailById(state) {
      return (orderNumber) => {
        return state.orders[orderNumber] ? state.orders[orderNumber] : {}
      }
    },
    orderInfoById(state) {
      return (orderId) => {
        return state.detail[orderId] ? state.detail[orderId] : {}
      }
    },
    trackOrderDetailById(state) {
      return (orderId) => {
        if (state.recentUpdatedOrder) return state.recentUpdatedOrder

        return state.trackOrderDetail[orderId]
          ? state.trackOrderDetail[orderId]
          : {}
      }
    },
    subscriptionInfoById(state) {
      return (subscriptionId) => {
        return state.subscriptionDetail[subscriptionId]
          ? state.subscriptionDetail[subscriptionId]
          : ''
      }
    },
    editOrderInfoById(state) {
      return (orderId) => {
        return state.editDetail[orderId] ? state.editDetail[orderId] : {}
      }
    },
    selectedRecentSubstituteProduct(state) {
      return (orderLineItemId, preferredSubstituteProductID) => {
        const cartStore = useCartStore()
        if (
          cartStore.recentSubstituteDetail &&
          cartStore.recentSubstituteDetail.hasOwnProperty(orderLineItemId)
        )
          return cartStore.recentSubstituteDetail[orderLineItemId]
            .selectedSubstituteProduct

        let data = Object.values(cartStore.recentSubstituteDetail).find(
          (x) =>
            x.orderLineItemID == orderLineItemId ||
            x.previousOrderLineItemID == orderLineItemId
        )
        if (data) return data.selectedSubstituteProduct
        else if (preferredSubstituteProductID)
          return preferredSubstituteProductID

        return '00000000-0000-0000-0000-000000000000'
      }
    },
    slotInfoById(state) {
      return (orderId) => {
        return state.slotInfo[orderId] || null
      }
    },
    isSubstitutionAllowedByOrderId(state) {
      return (orderId) => {
        let isTrueFound = false,
          isFalseFound = false,
          orderInfo = this.editOrderInfoById(orderId)

        if (
          orderInfo &&
          orderInfo.trackOrderLineItems &&
          orderInfo.codeName != 'sd'
        ) {
          for (let item of orderInfo.trackOrderLineItems) {
            if (item.isDeleted) {
              continue
            }
            if ([2, 3].includes(item.orderLineItemOrderType)) {
              continue
            }
            if (item.allowSubstitute) {
              isTrueFound = true
            } else {
              isFalseFound = true
            }
          }

          if (isTrueFound && isFalseFound) {
            return null
          }
        }
        return isTrueFound
      }
    },
    alertData: (state) => (alertKey) => state.alertInfo[alertKey] || null,
    getUpdateTimeSlotByOrderId(state) {
      return (orderId) => {
        return !!state.updateTimeSlot[orderId]
      }
    },
    getPaymentDetailsForFulfillment() {
      return (fulfillmentCode) => {
        let orderDetail = this.orderDetailById(this.recentOrderNumber)
        let paymentType = 'CARD'

        if (orderDetail && orderDetail.orderFulfillments) {
          orderDetail.orderFulfillments.every(function (fulfillment) {
            if (
              fulfillment.codeName == fulfillmentCode &&
              fulfillment.isPayAtStore
            ) {
              paymentType = 'PAY_AT_STORE'
              return false
            }
            return true
          })
        }
        return paymentType
      }
    },
  },
  actions: {
    async getOrders(params) {
      try {
        let response = await this.$axiosInstance.get('/Order', { params })

        if (response?.data?.success) return response.data
        else return false
      } catch (error) {
        return error
      }
    },
    async deleteOrder({ orderId, customerID = null }) {
      try {
        let params = {}
        if (customerID) params.customerID = customerID
        let response = await this.$axiosInstance.delete(`/Order/${orderId}`, {
          params,
        })

        if (response?.data?.success) {
          if (Platform?.is?.mobile) {
            NotifyMethods.showSuccess(
              '<strong>Success!</strong> We have canceled your order.'
            )
          } else {
            this.setOrderAlert({
              alertKey: 'orderAlert',
              alertData: {
                variant: 'success',
                message:
                  '<strong>Success!</strong> We have canceled your order.',
              },
            })
          }
          return response.data
        } else {
          return false
        }
      } catch (error) {
        return error
      }
    },
    async trackOrder({ orderId, showLoader = true, invoiceError = false }) {
      try {
        hideShowFullSpinner(showLoader)
        let response = await this.$axiosInstance.get(
          `/Order/Detail/${orderId}`,
          {
            params: {
              WithBifurcation: true,
            },
          }
        )

        if (response?.data?.success) {
          let order = response.data.data
          if (order && order.trackOrderLineItems) {
            let breakIndex
            for (let i = order.trackOrderLineItems.length - 1; i >= 0; i--) {
              if (!order.trackOrderLineItems[i].isLinkedProduct) break
              else {
                order.trackOrderLineItems.every((trackOrderLineItem) => {
                  if (
                    trackOrderLineItem.orderLineItemID ==
                    order.trackOrderLineItems[i].parentLinkedLineItemId
                  ) {
                    if (!trackOrderLineItem.linkedProducts)
                      trackOrderLineItem.linkedProducts = []

                    trackOrderLineItem.linkedProducts.push(
                      order.trackOrderLineItems[i]
                    )
                    return false
                  }

                  return true
                })

                breakIndex = i
              }
            }

            if (breakIndex) {
              order.trackOrderLineItems = order.trackOrderLineItems.splice(
                0,
                breakIndex
              )
            }
          }

          const trackOrderDetail = { [orderId]: order }
          this.trackOrderDetail = Object.assign(
            {},
            this.trackOrderDetail,
            trackOrderDetail
          )
          return response.data.data
        } else {
          return false
        }
      } catch (error) {
        if (invoiceError) {
          if (Platform?.is?.mobile) {
            NotifyMethods.showError(
              '<strong>Error!</strong> Sorry! PDF is not available.'
            )
          } else {
            this.setOrderAlert({
              alertKey: 'trackOrderAlert',
              alertData: {
                variant: 'danger',
                message: '<strong>Error!</strong> Sorry! PDF is not available.',
              },
            })
          }
        }
        hideShowFullSpinner(false)
        return false
      } finally {
        hideShowFullSpinner(false)
      }
    },
    async editOrderDetail(params) {
      try {
        hideShowFullSpinner(true)

        let response = await this.$axiosInstance.get('/Order/Edit/Detail', {
          params,
        })

        delete this.updateTimeSlot[params.OrderID]

        if (response && response.data && response.data.success) {
          let order = response.data.data
          if (order && order.trackOrderLineItems) {
            let breakIndex
            for (let i = order.trackOrderLineItems.length - 1; i >= 0; i--) {
              if (!order.trackOrderLineItems[i].isLinkedProduct) break
              else {
                order.trackOrderLineItems.every((trackOrderLineItem) => {
                  if (
                    trackOrderLineItem.orderLineItemID ==
                    order.trackOrderLineItems[i].parentLinkedLineItemId
                  ) {
                    if (!trackOrderLineItem.linkedProducts)
                      trackOrderLineItem.linkedProducts = []

                    trackOrderLineItem.linkedProducts.push(
                      order.trackOrderLineItems[i]
                    )
                    return false
                  }

                  return true
                })

                breakIndex = i
              }
            }

            if (breakIndex) {
              order.trackOrderLineItems = order.trackOrderLineItems.splice(
                0,
                breakIndex
              )
            }
          }

          order.trackOrderLineItems.forEach((item) => {
            item.substituteProductListData.forEach((x) => {
              if (
                x.substituteProductDetails &&
                x.substituteProductDetails.length > 0
              ) {
                x.substituteProductDetails.push({
                  image: '',
                  price: 0,
                  productID: '00000000-0000-0000-0000-000000000000',
                  productName: 'No Preference',
                  qtyOnHand: 0,
                  seoName: '',
                  substituteProductID: '00000000-0000-0000-0000-000000000000',
                })
              }
            })
          })

          this.setEditOrderInfo({ [params.OrderID]: order })
          return response.data.data
        } else {
          return false
        }
      } catch (error) {
        return false
      } finally {
        hideShowFullSpinner(false)
      }
    },
    async viewSubscriptionDetail({ subscriptionId, showLoader = true }) {
      const commonStore = useCommonStore()

      hideShowFullSpinner(showLoader)
      try {
        let response = await this.$axiosInstance.get(
          `/AutoShipOrder/Detail/${subscriptionId}`
        )
        let subscriptionDetails = response.data.data
        this.subscriptionDetail = Object.assign({}, this.subscriptionDetail, {
          [subscriptionId]: subscriptionDetails,
        })
        if (Platform?.is?.mobile && subscriptionDetails.productName) {
          commonStore.setHeaderTitle({ title: subscriptionDetails.productName })
        }
        return response.data.data
      } catch (err) {
        console.log('err', err)
        hideShowFullSpinner(false)
      } finally {
        hideShowFullSpinner(false)
      }
    },
    async getUpcomingOrders(subscriptionId) {
      hideShowFullSpinner(true)
      try {
        let response = await this.$axiosInstance.get(
          `/AutoShipOrder/UpcomingOrders/${subscriptionId}`
        )

        return response.data.data
      } catch (err) {
        console.log('err', err)
      } finally {
        hideShowFullSpinner(false)
      }
    },
    async getPreviousOrders({ subscriptionId, params }) {
      try {
        let { data } = await this.$axiosInstance.get(
          `/AutoShipOrder/PreviousOrders/${subscriptionId}`,
          { params }
        )

        return data
      } catch (err) {
        console.log('err', err)
      }
    },
    async viewOrderDetail(orderId) {
      try {
        let response = await this.$axiosInstance.get(
          `/Order/ViewDetail/${orderId}`
        )

        if (response?.data?.success) {
          let order = response.data.data

          if (order && order.trackOrderLineItems) {
            let breakIndex
            for (let i = order.trackOrderLineItems.length - 1; i >= 0; i--) {
              if (!order.trackOrderLineItems[i].isLinkedProduct) break
              else {
                order.trackOrderLineItems.every((trackOrderLineItem) => {
                  if (
                    trackOrderLineItem.orderLineItemID ==
                    order.trackOrderLineItems[i].parentLinkedLineItemId
                  ) {
                    if (!trackOrderLineItem.linkedProducts)
                      trackOrderLineItem.linkedProducts = []

                    trackOrderLineItem.linkedProducts.push(
                      order.trackOrderLineItems[i]
                    )
                    return false
                  }

                  return true
                })

                breakIndex = i
              }
            }

            if (breakIndex) {
              order.trackOrderLineItems = order.trackOrderLineItems.splice(
                0,
                breakIndex
              )
            }
          }

          if (order && order.returnOrderLineItems) {
            let breakIndex
            for (let i = order.returnOrderLineItems.length - 1; i >= 0; i--) {
              if (!order.returnOrderLineItems[i].isLinkedProduct) break
              else {
                order.returnOrderLineItems.every((returnOrderLineItem) => {
                  if (
                    returnOrderLineItem.orderLineItemID ==
                    order.returnOrderLineItems[i].parentLinkedLineItemId
                  ) {
                    if (!returnOrderLineItem.linkedProducts)
                      returnOrderLineItem.linkedProducts = []

                    returnOrderLineItem.linkedProducts.push(
                      order.returnOrderLineItems[i]
                    )
                    return false
                  }

                  return true
                })

                breakIndex = i
              }
            }

            if (breakIndex) {
              order.returnOrderLineItems = order.returnOrderLineItems.splice(
                0,
                breakIndex
              )
            }
          }
          if (order && order.notDeliveredOrderLineItems) {
            let breakIndex
            for (
              let i = order.notDeliveredOrderLineItems.length - 1;
              i >= 0;
              i--
            ) {
              if (!order.notDeliveredOrderLineItems[i].isLinkedProduct) break
              else {
                order.notDeliveredOrderLineItems.every(
                  (notDeliveredOrderLineItem) => {
                    if (
                      notDeliveredOrderLineItem.orderLineItemID ==
                      order.notDeliveredOrderLineItems[i].parentLinkedLineItemId
                    ) {
                      if (!notDeliveredOrderLineItem.linkedProducts)
                        notDeliveredOrderLineItem.linkedProducts = []

                      notDeliveredOrderLineItem.linkedProducts.push(
                        order.notDeliveredOrderLineItems[i]
                      )
                      return false
                    }

                    return true
                  }
                )

                breakIndex = i
              }
            }

            if (breakIndex) {
              order.notDeliveredOrderLineItems =
                order.notDeliveredOrderLineItems.splice(0, breakIndex)
            }
          }

          this.detail = Object.assign({}, this.detail, {
            [orderId]: order,
          })

          return response.data.data
        } else {
          return false
        }
      } catch (error) {
        return false
      }
    },
    async toggleSubstitutionByOrderId({ orderId, isSubstitutionAllowed }) {
      try {
        let orderInfo = this.editOrderInfoById(orderId),
          objRequest = {}

        isSubstitutionAllowed = isSubstitutionAllowed || false

        if (orderInfo && orderInfo.orderNumber) {
          objRequest.orderID = orderId
          objRequest.editOrderLineItems = []

          for (let item of orderInfo.trackOrderLineItems) {
            if (
              item.allowSubstitute == isSubstitutionAllowed &&
              !item.isDeleted &&
              ![2, 3].includes(item.orderLineItemOrderType)
            ) {
              objRequest.editOrderLineItems.push({
                productID: item.productId,
                allowSubstitute: !isSubstitutionAllowed,
              })
            }
          }
        }

        await this.updateOrder(objRequest)
      } catch (error) {
        return error
      }
    },
    async updateOrder(params) {
      try {
        hideShowFullSpinner(true)
        let response = await this.$axiosInstance.post('Order/Edit', params)

        if (response.data?.success) {
          let order = response.data.data
          if (order && order.trackOrderLineItems) {
            let breakIndex
            for (let i = order.trackOrderLineItems.length - 1; i >= 0; i--) {
              if (!order.trackOrderLineItems[i].isLinkedProduct) break
              else {
                order.trackOrderLineItems.every((trackOrderLineItem) => {
                  if (
                    trackOrderLineItem.orderLineItemID ==
                    order.trackOrderLineItems[i].parentLinkedLineItemId
                  ) {
                    if (!trackOrderLineItem.linkedProducts)
                      trackOrderLineItem.linkedProducts = []

                    trackOrderLineItem.linkedProducts.push(
                      order.trackOrderLineItems[i]
                    )
                    return false
                  }

                  return true
                })

                breakIndex = i
              }
            }

            if (breakIndex) {
              order.trackOrderLineItems = order.trackOrderLineItems.splice(
                0,
                breakIndex
              )
            }
          }

          order.trackOrderLineItems.forEach((item) => {
            item.substituteProductListData.forEach((x) => {
              if (
                x.substituteProductDetails &&
                x.substituteProductDetails.length > 0
              ) {
                x.substituteProductDetails.push({
                  image: '',
                  price: 0,
                  productID: '00000000-0000-0000-0000-000000000000',
                  productName: 'No Preference',
                  qtyOnHand: 0,
                  seoName: '',
                  substituteProductID: '00000000-0000-0000-0000-000000000000',
                })
              }
            })
          })

          this.setEditOrderInfo({ [params.orderID]: order })
          return { success: true }
        } else {
          return response.data
        }
      } catch (error) {
        if (error.errors && error.errors.length) {
          error.errors.forEach((error) => {
            if (
              error.errorCode !== 'CRT_0114' &&
              error.errorCode !== 'CRT_1010'
            ) {
              NotifyMethods.showError(error.descreption)
            }
          })
        }
        return { success: false, errors: error.errors || [] }
      } finally {
        hideShowFullSpinner(false)
      }
    },
    async getProducts(params) {
      try {
        const persistedStore = usePersistedStore()
        let response = await this.$axiosInstance.get('Order/Edit/Products', {
          params: {
            ...params,
            zipCode: persistedStore.locationOrShippingZip,
          },
        })

        if (response?.data?.success) {
          return response.data.data
        } else {
          return false
        }
      } catch (error) {
        return error
      }
    },
    async clearRecentItems(orderId) {
      try {
        let response = await this.$axiosInstance.delete(`Order/Edit/${orderId}`)

        if (response?.data?.success) return true
        else return false
      } catch (error) {
        return error
      }
    },
    async applyOrderChanges(params) {
      try {
        hideShowFullSpinner(true)
        let cancelOrder = params.cancelOrder
        delete params.cancelOrder
        let response = await this.$axiosInstance.post('Order/Update', params)

        if (response) {
          if (response.data?.success) {
            let order = response.data.data
            if (order && order.trackOrderLineItems) {
              let breakIndex
              for (let i = order.trackOrderLineItems.length - 1; i >= 0; i--) {
                if (!order.trackOrderLineItems[i].isLinkedProduct) break
                else {
                  order.trackOrderLineItems.every((trackOrderLineItem) => {
                    if (
                      trackOrderLineItem.orderLineItemID ==
                      order.trackOrderLineItems[i].parentLinkedLineItemId
                    ) {
                      if (!trackOrderLineItem.linkedProducts)
                        trackOrderLineItem.linkedProducts = []

                      trackOrderLineItem.linkedProducts.push(
                        order.trackOrderLineItems[i]
                      )
                      return false
                    }

                    return true
                  })

                  breakIndex = i
                }
              }

              if (breakIndex) {
                order.trackOrderLineItems = order.trackOrderLineItems.splice(
                  0,
                  breakIndex
                )
              }
            }

            if (Platform?.is?.mobile) {
              if (cancelOrder) {
                NotifyMethods.showSuccess(
                  '<strong>Success!</strong> We have canceled your order.'
                )
              } else {
                NotifyMethods.showSuccess(
                  '<strong>Success!</strong> We Updated your order.'
                )
              }
            } else {
              if (cancelOrder) {
                this.setOrderAlert({
                  alertKey: 'orderAlert',
                  alertData: {
                    variant: 'success',
                    message:
                      '<strong>Success!</strong> We have canceled your order.',
                  },
                })
              } else {
                this.setOrderAlert({
                  alertKey: 'trackOrderAlert',
                  alertData: {
                    variant: 'success',
                    message: '<strong>Success!</strong> We Updated your order.',
                  },
                })
              }
            }

            if (!cancelOrder) this.setRecentUpdatedOrder(order)
          }
          return true
        } else {
          return false
        }
      } catch (error) {
        if (error.errors && error.errors.length > 0) {
          if (Platform?.is?.mobile) {
            NotifyMethods.showError(
              `<strong>Error!</strong> ${error.errors[0].descreption}`
            )
          } else {
            this.setOrderAlert({
              alertKey: 'editOrderAlert',
              alertData: {
                variant: 'danger',
                message: `<strong>Error!</strong> ${error.errors[0].descreption}`,
              },
            })
          }
        } else if (error && error.message) {
          if (Platform?.is?.mobile) {
            NotifyMethods.showError(`<strong>Error!</strong> ${error.message}`)
          } else {
            this.setOrderAlert({
              alertKey: 'editOrderAlert',
              alertData: {
                variant: 'danger',
                message: `<strong>Error!</strong> ${error.message}`,
              },
            })
          }
        }
        return false
      } finally {
        hideShowFullSpinner(false)
      }
    },
    async changeArrivalStatus(body) {
      try {
        hideShowFullSpinner(true)

        let { orderID } = body,
          { data } = await this.$axiosInstance.put(
            '/Order/StorePickup/ArrivalStatus',
            body
          )

        if (data?.success)
          this.updateTrackOrderDetail({
            orderId: orderID,
            showIamonMyway: false,
            showIamHere: true,
          })

        return data
      } catch (error) {
        return error
      } finally {
        hideShowFullSpinner(false)
      }
    },
    async allowDeclineSubstitution(params) {
      try {
        hideShowFullSpinner(true)
        let { data } = await this.$axiosInstance.put(
          'Order/AllowDeclineSubstitution',
          params
        )

        if (data?.success && data.data?.orderID) {
          let order = data.data
          if (order?.trackOrderLineItems) {
            let breakIndex
            for (let i = order.trackOrderLineItems.length - 1; i >= 0; i--) {
              if (!order.trackOrderLineItems[i].isLinkedProduct) break
              else {
                order.trackOrderLineItems.every((trackOrderLineItem) => {
                  if (
                    trackOrderLineItem.orderLineItemID ==
                    order.trackOrderLineItems[i].parentLinkedLineItemId
                  ) {
                    if (!trackOrderLineItem.linkedProducts)
                      trackOrderLineItem.linkedProducts = []

                    trackOrderLineItem.linkedProducts.push(
                      order.trackOrderLineItems[i]
                    )
                    return false
                  }

                  return true
                })

                breakIndex = i
              }
            }

            if (breakIndex) {
              order.trackOrderLineItems = order.trackOrderLineItems.splice(
                0,
                breakIndex
              )
            }
          }

          this.trackOrderDetail = Object.assign({}, this.trackOrderDetail, {
            [data.data.orderID]: order,
          })
          return true
        }
      } catch (error) {
        return false
      } finally {
        hideShowFullSpinner(false)
      }
    },
    async getParkingSpot(params) {
      try {
        // if (!getters.parkingSpots) {
        let { data } = await this.$axiosInstance.get(
          '/Order/StorePickup/PickupSpot',
          { params }
        )
        this.parkingSpots = data.data
        return data
        // }
      } catch (error) {
        return error
      }
    },
    async availableSlots(params) {
      try {
        this.loadingSlot = true
        let { data } = await this.$axiosInstance.get('Order/Edit/Slots', {
          params,
        })
        if (data.success) {
          this.slotInfo = Object.assign({}, this.slotInfo, {
            [params.orderId]: data.data,
          })
          return data
        }
      } catch (error) {
        return error
      } finally {
        this.loadingSlot = false
      }
    },
    async updateReservedSlot(params) {
      try {
        let { data } = await this.$axiosInstance.post(
          'Order/UpdateReservedSlot',
          params
        )

        if (data.success) {
          if (Platform?.is?.mobile) {
            NotifyMethods.showSuccess(
              '<strong>Success!</strong> We Updated your order time slot.'
            )
          } else {
            this.setOrderAlert({
              alertKey: 'editOrderAlert',
              alertData: {
                variant: 'success',
                message: '<strong>Success!</strong> We Updated your order.',
              },
            })
          }
          return data
        } else if (response.errors && response.errors.length > 0) {
          if (Platform?.is?.mobile) {
            NotifyMethods.showError(
              `<strong>Error!</strong>${response.errors[0].descreption}.`
            )
          } else {
            this.setOrderAlert({
              alertKey: 'orderTimeAlert',
              alertData: {
                variant: 'danger',
                message: `<strong>Error!</strong>${response.errors[0].descreption}.`,
              },
            })
          }
        }
      } catch (error) {
        if (Platform?.is?.mobile) {
          NotifyMethods.showError(`<strong>Error!</strong> ${error.message}.`)
        } else {
          this.setOrderAlert({
            alertKey: 'orderTimeAlert',
            alertData: {
              variant: 'danger',
              message: `<strong>Error!</strong> ${error.message}.`,
            },
          })
        }
        return error
      }
    },
    async setParkingSpot(body) {
      try {
        let { orderID } = body,
          { data } = await this.$axiosInstance.put(
            '/Order/StorePickup/PickupSpot',
            body
          )

        if (data && data.success) {
          this.updateTrackOrderDetail({
            orderId: orderID,
            showIamonMyway: false,
            showIamHere: false,
            iAmHereInfo: {
              ...data.data,
            },
          })
        }
        return data
      } catch (error) {
        return error
      }
    },
    async generateInvoice({
      orderId,
      showLoader = true,
      invoiceErrorAlertKey = '',
    }) {
      try {
        hideShowFullSpinner(showLoader)
        let response = await this.$axiosInstance.get(
          `/Order/PrintInvoice?orderId=${orderId}`,
          {
            responseType: 'blob',
          }
        )

        hideShowFullSpinner(false)
        return response
      } catch (error) {
        hideShowFullSpinner(false)
        if (Platform?.is?.mobile) {
          NotifyMethods.showError(
            '<strong>Error!</strong> Sorry! PDF is not available.'
          )
        } else {
          this.setOrderAlert({
            alertKey: invoiceErrorAlertKey,
            alertData: {
              variant: 'danger',
              message: '<strong>Error!</strong> Sorry! PDF is not available.',
            },
          })
        }
      }
    },
    async updateLNSDollars(params) {
      try {
        let response = await this.$axiosInstance.post(
          'Order/Edit/UpdateLNSDollars',
          params
        )

        if (response.data?.success) {
          let order = response.data.data
          if (order?.trackOrderLineItems) {
            let breakIndex
            for (let i = order.trackOrderLineItems.length - 1; i >= 0; i--) {
              if (!order.trackOrderLineItems[i].isLinkedProduct) break
              else {
                order.trackOrderLineItems.every((trackOrderLineItem) => {
                  if (
                    trackOrderLineItem.orderLineItemID ==
                    order.trackOrderLineItems[i].parentLinkedLineItemId
                  ) {
                    if (!trackOrderLineItem.linkedProducts)
                      trackOrderLineItem.linkedProducts = []

                    trackOrderLineItem.linkedProducts.push(
                      order.trackOrderLineItems[i]
                    )
                    return false
                  }

                  return true
                })

                breakIndex = i
              }
            }

            if (breakIndex) {
              order.trackOrderLineItems = order.trackOrderLineItems.splice(
                0,
                breakIndex
              )
            }
          }
          this.setEditOrderInfo({ [params.orderID]: order })
          return response.data
        }
      } catch (error) {
        return error
      }
    },
    async getLinkedProducts({ productID, locationID }) {
      try {
        let response = await this.$axiosInstance.get(
          `Product/LinkedProduct?productID=${productID}&LocationID=${locationID}`
        )

        return response
      } catch (error) {
        return error
      }
    },
    async GetCoupons(orderId) {
      try {
        let { data } = await this.$axiosInstance.get(
          `/Order/Edit/Coupons/${orderId}`
        )

        // if (data.success && data.data)
        //   commit('SET_COUPONS', data.data)

        return data
      } catch (error) {
        return error
      }
    },
    async applyCoupons({ orderId, payload }) {
      try {
        let { data } = await this.$axiosInstance.post(
          `/Order/Edit/Coupons/${orderId}`,
          payload
        )

        if (data.success && data.data)
          this.setEditOrderInfo({ [orderId]: data.data })

        return data
      } catch (error) {
        return error
      }
    },
    async removeCoupons(body) {
      try {
        let { data } = await this.$axiosInstance.delete(
          `/Order/Edit/Coupons/${body.couponId}?OrderID=${body.orderId}`
        )

        if (data.success && data.data)
          this.setEditOrderInfo({ [body.orderId]: data.data })

        return data
      } catch (error) {
        return error
      }
    },
    async applyReorder({ orderId, body }) {
      try {
        const cartStore = useCartStore()
        let response = await this.$axiosInstance.post(
          `Order/reorder/${orderId}`,
          body
        )

        if (
          response.data?.success &&
          response.data.data?.shoppingCartDetailViewModel
        ) {
          let cartDetails = response.data.data.shoppingCartDetailViewModel
          cartStore.setField({
            field: 'cartNumber',
            value: cartDetails.orderNumber,
          })
          cartStore.pushCartDetail({
            cartId: cartDetails.orderNumber,
            cartDetail: cartDetails,
          })
        }
        return response.data
      } catch (error) {
        return error
      }
    },
    async getCarMakeModel(params) {
      try {
        let response = await this.$axiosInstance.get(
          '/Order/CarMakeModel',
          params
        )

        return response.data
      } catch (error) {
        return error
      }
    },
    async editOrderUpdateSubstitute(params) {
      const cartStore = useCartStore()
      let recentSubstitutions = cartStore.recentSubstituteDetail
      if (
        Object.keys(recentSubstitutions) &&
        Object.keys(recentSubstitutions).length > 0
      ) {
        cartStore.resetRecentSubstitute()
        let substitute = {}
        let substituteParams = []

        params.forEach((x) => {
          if (recentSubstitutions[x.orderLineItemID]) {
            substitute[x.orderLineItemID] = {
              allowSubstitute:
                recentSubstitutions[x.orderLineItemID].allowSubstitute,
              selectedSubstituteProduct:
                recentSubstitutions[x.orderLineItemID]
                  .selectedSubstituteProduct ==
                '00000000-0000-0000-0000-000000000000'
                  ? null
                  : recentSubstitutions[x.orderLineItemID]
                      .selectedSubstituteProduct,
              orderLineItemID:
                recentSubstitutions[x.orderLineItemID].orderLineItemID,
              substituteProducts:
                recentSubstitutions[x.orderLineItemID].substituteProducts,
            }

            substituteParams = [
              ...substituteParams,
              {
                allowSubstitute:
                  recentSubstitutions[x.orderLineItemID].allowSubstitute,
                orderLineItemID: x.orderLineItemID,
                substituteProductID:
                  recentSubstitutions[x.orderLineItemID]
                    .selectedSubstituteProduct ==
                  '00000000-0000-0000-0000-000000000000'
                    ? null
                    : recentSubstitutions[x.orderLineItemID]
                        .selectedSubstituteProduct,
              },
            ]
          }
        })

        cartStore.setRecentSubstitute(substitute)
        return substituteParams
      }
    },
    async trackGuestOrder({
      orderId,
      orderNumber,
      showLoader = true,
      invoiceError = false,
    }) {
      try {
        hideShowFullSpinner(showLoader)
        let response = await this.$axiosInstance.get(
          `/Order/Guest/${orderId}`,
          {
            params: {
              orderNumber,
            },
          }
        )

        if (response?.data?.success) {
          let order = response.data.data
          if (order && order.trackOrderLineItems) {
            let breakIndex
            for (let i = order.trackOrderLineItems.length - 1; i >= 0; i--) {
              if (!order.trackOrderLineItems[i].isLinkedProduct) break
              else {
                order.trackOrderLineItems.every((trackOrderLineItem) => {
                  if (
                    trackOrderLineItem.orderLineItemID ==
                    order.trackOrderLineItems[i].parentLinkedLineItemId
                  ) {
                    if (!trackOrderLineItem.linkedProducts)
                      trackOrderLineItem.linkedProducts = []

                    trackOrderLineItem.linkedProducts.push(
                      order.trackOrderLineItems[i]
                    )
                    return false
                  }

                  return true
                })

                breakIndex = i
              }
            }

            if (breakIndex) {
              order.trackOrderLineItems = order.trackOrderLineItems.splice(
                0,
                breakIndex
              )
            }
          }

          this.trackOrderDetail = Object.assign({}, this.trackOrderDetail, {
            [orderId]: order,
          })
          return response.data.data
        } else {
          return false
        }
      } catch (error) {
        if (invoiceError) {
          if (Platform && Platform.is && Platform.is.mobile) {
            NotifyMethods.showError(
              '<strong>Error!</strong> Sorry! PDF is not available.'
            )
          } else {
            this.setOrderAlert({
              alertKey: 'trackOrderAlert',
              alertData: {
                variant: 'danger',
                message: '<strong>Error!</strong> Sorry! PDF is not available.',
              },
            })
          }
        }
        hideShowFullSpinner(false)
        return false
      } finally {
        hideShowFullSpinner(false)
      }
    },
    async applyDiscountCoupons({ orderId, payload }) {
      try {
        let { data } = await this.$axiosInstance.post(
          `/Order/Edit/DiscountCoupons/${orderId}`,
          payload
        )

        if (data.success && data.data) {
          let order = data.data
          if (order?.trackOrderLineItems) {
            let breakIndex
            for (let i = order.trackOrderLineItems.length - 1; i >= 0; i--) {
              if (!order.trackOrderLineItems[i].isLinkedProduct) break
              else {
                order.trackOrderLineItems.every((trackOrderLineItem) => {
                  if (
                    trackOrderLineItem.orderLineItemID ==
                    order.trackOrderLineItems[i].parentLinkedLineItemId
                  ) {
                    if (!trackOrderLineItem.linkedProducts)
                      trackOrderLineItem.linkedProducts = []

                    trackOrderLineItem.linkedProducts.push(
                      order.trackOrderLineItems[i]
                    )
                    return false
                  }

                  return true
                })

                breakIndex = i
              }
            }

            if (breakIndex) {
              order.trackOrderLineItems = order.trackOrderLineItems.splice(
                0,
                breakIndex
              )
            }
          }
          this.setEditOrderInfo({ [orderId]: order })
          return data
        }
      } catch (error) {
        return error
      }
    },
    async removeDiscountCoupons(body) {
      try {
        let { data } = await this.$axiosInstance.delete(
          `/Order/Edit/DiscountCoupons/${body.couponId}?orderId=${body.orderId}`
        )

        if (data.success && data.data) {
          let order = data.data
          if (order?.trackOrderLineItems) {
            let breakIndex
            for (let i = order.trackOrderLineItems.length - 1; i >= 0; i--) {
              if (!order.trackOrderLineItems[i].isLinkedProduct) break
              else {
                order.trackOrderLineItems.every((trackOrderLineItem) => {
                  if (
                    trackOrderLineItem.orderLineItemID ==
                    order.trackOrderLineItems[i].parentLinkedLineItemId
                  ) {
                    if (!trackOrderLineItem.linkedProducts)
                      trackOrderLineItem.linkedProducts = []

                    trackOrderLineItem.linkedProducts.push(
                      order.trackOrderLineItems[i]
                    )
                    return false
                  }

                  return true
                })

                breakIndex = i
              }
            }

            if (breakIndex) {
              order.trackOrderLineItems = order.trackOrderLineItems.splice(
                0,
                breakIndex
              )
            }
          }
          this.setEditOrderInfo({ [body.orderId]: order })
          return data
        }
      } catch (error) {
        return error
      }
    },
    async getSubscriptions(params) {
      try {
        let response = await this.$axiosInstance.get('/AutoShipOrder', {
          params,
        })

        if (response?.data?.success) return response.data
        else return false
      } catch (error) {
        return error
      }
    },
    async changeSubscriptionOption({ id, params = {} }) {
      try {
        let response = await this.$axiosInstance.put(
          `/AutoShipOrder/SubscriptionPauseOrCancel/${id}`,
          params
        )

        if (response?.data?.success) return response.data
        else return false
      } catch (error) {
        return error
      }
    },
    async updateSubscriptionAction({ id, params = {} }) {
      try {
        let response = await this.$axiosInstance.put(
          `/AutoShipOrder/Subscription/${id}`,
          params
        )

        if (response?.data?.success) return response.data
        else return false
      } catch (error) {
        return error
      }
    },
    async upcomingOrderAction({ id, params = {} }) {
      try {
        let response = await this.$axiosInstance.put(
          `/AutoShipOrder/UpcomingorderAction/${id}`,
          params
        )

        if (response?.data?.success) return response.data
        else return false
      } catch (error) {
        return error
      }
    },
    async updateSubscriptionAddressPayment({ id, params = {} }) {
      try {
        let response = await this.$axiosInstance.put(
          `/AutoShipOrder/ManageAddressOrPayment/${id}`,
          params
        )

        if (response?.data?.success) return response.data
        else return false
      } catch (error) {
        return error
      }
    },
    async getSubscriptionDiscounts(params = {}) {
      try {
        let { data } = await this.$axiosInstance.get(
          '/AutoShipOrder/OrderDiscount/Details',
          { params }
        )

        return data
      } catch (error) {
        return error
      }
    },
    setOrderAlert({ alertKey, alertData }) {
      if (typeof alertData === 'object' && alertData !== null)
        this.alertInfo = { ...this.alertInfo, [alertKey]: alertData }
      else this.alertInfo = { ...this.alertInfo, [alertKey]: null }
    },
    setEditOrderInfo(detail) {
      try {
        const cartStore = useCartStore()
        cartStore.updateRecentSubstituteOnEditOrder(detail)
        this.editDetail = Object.assign({}, this.editDetail, detail)
      } catch {}
    },
    setRecentUpdatedOrder(orderDetail) {
      if (orderDetail) this.recentUpdatedOrder = Object.assign({}, orderDetail)
      else this.recentUpdatedOrder = null
    },
    updateTrackOrderDetail(updatedDetail) {
      let { orderId } = updatedDetail

      if (orderId && this.trackOrderDetail[orderId])
        this.trackOrderDetail[orderId] = Object.assign(
          {},
          this.trackOrderDetail[orderId],
          updatedDetail
        )
    },
    deleteRecentItem(productId) {
      delete this.recentItems[productId]
    },
    deleteOrderDetail(orderNumber) {
      delete this.orders[orderNumber]
    },
    setRecentItems(item) {
      this.recentItems = Object.assign({}, this.recentItems, item)
    },
    resetRecentItem() {
      this.recentItems = Object.assign({})
    },
    setField({ field, value }) {
      this[field] = value
    },
    setUpdateTimeSlot(orderId) {
      this.updateTimeSlot = { ...this.updateTimeSlot, [orderId]: true }
    },
    clearTrackOrderDetail(orderId) {
      delete this.slotInfo[orderId]
    },
    setOrderCategories(categories) {
      this.categories = [
        {
          categoryID: '',
          categoryName: 'All categories',
          hasChild: false,
        },
        ...categories,
      ]
    },
    setOrderDetail(orderDetail) {
      for (const orderNum in orderDetail) {
        orderDetail[orderNum].orderFulfillments.forEach((fulfillment) => {
          let breakIndex

          for (
            let i = fulfillment.orderLineItemsModelsList.length - 1;
            i >= 0;
            i--
          ) {
            if (!fulfillment.orderLineItemsModelsList[i].isLinkedProduct) break
            else {
              fulfillment.orderLineItemsModelsList.every(function (
                orderLineItemsModel
              ) {
                if (
                  orderLineItemsModel.orderLineItemID ==
                  fulfillment.orderLineItemsModelsList[i].parentLinkedLineItemId
                ) {
                  if (!orderLineItemsModel.linkedProducts)
                    orderLineItemsModel.linkedProducts = []

                  orderLineItemsModel.linkedProducts.push(
                    fulfillment.orderLineItemsModelsList[i]
                  )

                  return false
                }
                return true
              })
              breakIndex = i
            }
          }

          if (breakIndex) {
            fulfillment.orderLineItemsModelsList =
              fulfillment.orderLineItemsModelsList.splice(0, breakIndex)
          }
        })
      }

      this.orders = Object.assign({}, this.orders, orderDetail)
    },
    async updateGiftCardForSubscription({ subscriptionId, giftCardIds }) {
      try {
        const { data } = await this.$axiosInstance.put(
          `/AutoShipOrder/GiftCards/${subscriptionId}`,
          { giftCardIds: [...giftCardIds] }
        )

        return data
      } catch (error) {
        return error
      }
    },
    setGiftCardAcknowlegment(val) {
      this.isGiftCardAllowForRecurringOrder = val
    },
  },
})
